import React from 'react';
import { parseAtMentions } from './mentions';

/**
 * Parse URLs from text
 * Returns array of {url, startIndex, endIndex}
 */
function parseURLs(text: string): Array<{ url: string; startIndex: number; endIndex: number }> {
  // URL regex that matches http://, https://, and www. URLs
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const urls: Array<{ url: string; startIndex: number; endIndex: number }> = [];
  let match;

  while ((match = urlRegex.exec(text)) !== null) {
    urls.push({
      url: match[0],
      startIndex: match.index,
      endIndex: match.index + match[0].length,
    });
  }

  return urls;
}

/**
 * Renders text with @mentions and URLs as clickable links
 * @param text - The text containing @mentions and URLs
 * @param onMentionClick - Callback when a mention is clicked
 * @returns JSX with mentions and URLs rendered as links
 */
export function renderMentionsAsLinks(
  text: string,
  onMentionClick: (username: string) => void
): React.ReactNode {
  if (!text) return text;

  const mentions = parseAtMentions(text);
  const urls = parseURLs(text);

  // Combine mentions and URLs, then sort by startIndex
  const allLinks: Array<{
    type: 'mention' | 'url';
    startIndex: number;
    endIndex: number;
    data: any;
  }> = [
    ...mentions.map(m => ({ type: 'mention' as const, startIndex: m.startIndex, endIndex: m.endIndex, data: m })),
    ...urls.map(u => ({ type: 'url' as const, startIndex: u.startIndex, endIndex: u.endIndex, data: u })),
  ].sort((a, b) => a.startIndex - b.startIndex);

  if (allLinks.length === 0) {
    return text;
  }

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  allLinks.forEach((link, index) => {
    // Skip if this link overlaps with the previous one
    if (link.startIndex < lastIndex) {
      return;
    }

    // Add text before the link
    if (link.startIndex > lastIndex) {
      parts.push(text.substring(lastIndex, link.startIndex));
    }

    if (link.type === 'mention') {
      // Add the mention as a clickable button
      parts.push(
        <button
          key={`mention-${index}`}
          onClick={(e) => {
            e.stopPropagation();
            onMentionClick(link.data.username);
          }}
          className="text-accent-600 hover:text-accent-700 hover:underline font-medium"
        >
          @{link.data.username}
        </button>
      );
    } else if (link.type === 'url') {
      // Add the URL as a clickable link
      const url = link.data.url;
      const href = url.startsWith('http') ? url : `https://${url}`;

      parts.push(
        <a
          key={`url-${index}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-accent-600 hover:text-accent-700 underline break-all"
        >
          {url}
        </a>
      );
    }

    lastIndex = link.endIndex;
  });

  // Add remaining text after the last link
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts;
}

/**
 * Simple renderer that just highlights @mentions without making them clickable
 * @param text - The text containing @mentions
 * @returns JSX with mentions highlighted
 */
export function highlightMentions(text: string): React.ReactNode {
  if (!text) return text;

  const mentions = parseAtMentions(text);

  if (mentions.length === 0) {
    return text;
  }

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  mentions.forEach((mention, index) => {
    // Add text before the mention
    if (mention.startIndex > lastIndex) {
      parts.push(text.substring(lastIndex, mention.startIndex));
    }

    // Add the mention as highlighted text
    parts.push(
      <span
        key={`mention-${index}`}
        className="text-accent-600 font-medium"
      >
        @{mention.username}
      </span>
    );

    lastIndex = mention.endIndex;
  });

  // Add remaining text after the last mention
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts;
}